import React from 'react';
import { TableBody, TableRow, TableCell, IconButton, Box, Typography } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import type { Table, Column } from '@tanstack/react-table';
import type { GroupBucket } from '../../../hooks/useGridGrouping';
import { getShowInGroupFooter } from '../../../hooks/useGridGrouping';
import type { ColumnMetadata } from '../../../types';
import { getColumnWidth, isGroupSummaryColumn } from '../utils/columnUtils';
import { DataRow } from './DataGridBody';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type SharedProps<T> = {
  table: Table<T>;
  collapsedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  columnMetadataMap: Map<string, ColumnMetadata>;
  groupCaptions: Map<string, string>;
  modifiedCells?: Set<string>;
  markedForDeletion?: Set<string>;
  rowHeight: number;
  wordWrap?: boolean;
  hiddenColumnIds?: Set<string>;
};

type GroupedDataGridBodyProps<T> = SharedProps<T> & {
  groupedRows: GroupBucket<T>[];
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const formatSummaryValue = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * Alpha for the group-header row background — increases slightly with depth
 * so nested groups are visually distinguishable.
 */
const rowAlpha = (depth: number, isDark: boolean) =>
  isDark
    ? 0.06 + depth * 0.03 // dark mode: lighter overlays
    : 0.04 + depth * 0.02; // light mode: darker overlays

// ─────────────────────────────────────────────────────────────────────────────
// Inner recursive component
// ─────────────────────────────────────────────────────────────────────────────

type GroupRowsProps<T> = SharedProps<T> & {
  groups: GroupBucket<T>[];
  depth: number;
  rowOffset: number;
};

const getRenderedRowCount = <T,>(group: GroupBucket<T>, collapsedGroups: Set<string>): number => {
  if (collapsedGroups.has(group.key)) return 0;
  if (group.children.length === 0) return group.rows.length;

  return group.children.reduce(
    (count, child) => count + getRenderedRowCount(child, collapsedGroups),
    0
  );
};

/** Renders one level of groups + recurses into children or leaf rows. */
function GroupRows<T extends Record<string, unknown>>(props: GroupRowsProps<T>) {
  const {
    groups,
    depth,
    table,
    collapsedGroups,
    onToggleGroup,
    columnMetadataMap,
    groupCaptions,
    modifiedCells,
    markedForDeletion,
    rowHeight,
    wordWrap,
    hiddenColumnIds,
    rowOffset,
  } = props;

  const columns = table
    .getVisibleLeafColumns()
    .filter((col) => !hiddenColumnIds?.has(col.id)) as Column<T, unknown>[];
  const columnOrder = table.getState().columnOrder;
  const columnVisibility = table.getState().columnVisibility;

  /** Columns that show a sum row *below* the group (showInGroupFooter = true) */
  const footerSumColumns = columns.filter((col) => {
    const meta = columnMetadataMap.get(col.id);
    return isGroupSummaryColumn(meta) && getShowInGroupFooter(meta!);
  });

  /** Left padding for the first cell at this depth */
  const firstCellPl = 8 + depth * 20;

  return (
    <>
      {groups.map((group, groupIndex) => {
        const groupRowOffset =
          rowOffset +
          groups
            .slice(0, groupIndex)
            .reduce(
              (count, previousGroup) => count + getRenderedRowCount(previousGroup, collapsedGroups),
              0
            );
        const isCollapsed = collapsedGroups.has(group.key);
        const isLeaf = group.children.length === 0;
        const caption = groupCaptions.get(group.fieldName) ?? group.fieldName;
        const inlineSummaryColumns = columns.filter((col) => {
          const meta = columnMetadataMap.get(col.id);
          return isGroupSummaryColumn(meta) && !getShowInGroupFooter(meta!);
        });

        return (
          <React.Fragment key={group.key}>
            <TableRow
              hover
              onClick={() => onToggleGroup(group.key)}
              style={{ ...(wordWrap ? {} : { height: `${rowHeight}px` }), cursor: 'pointer' }}
              sx={{
                backgroundColor: (theme) =>
                  `rgba(${theme.palette.mode === 'dark' ? '255,255,255' : '0,0,0'},${rowAlpha(depth, theme.palette.mode === 'dark')})`,
              }}
            >
              {(() => {
                const firstSummaryIndex = columns.findIndex((col) =>
                  inlineSummaryColumns.some((summaryColumn) => summaryColumn.id === col.id)
                );
                const labelSpan =
                  firstSummaryIndex === -1 ? columns.length : Math.max(1, firstSummaryIndex);
                const firstColumnSummary =
                  firstSummaryIndex === 0 ? inlineSummaryColumns[0] : undefined;
                const firstColumnSummaryMeta = firstColumnSummary
                  ? columnMetadataMap.get(firstColumnSummary.id)
                  : undefined;
                const firstColumnSummaryField = firstColumnSummaryMeta
                  ? ((firstColumnSummaryMeta.dataField ||
                      firstColumnSummaryMeta.field ||
                      '') as string)
                  : '';

                return [
                  <TableCell
                    key={`${group.key}-label`}
                    colSpan={labelSpan}
                    sx={{
                      borderLeft: 0,
                      borderRight: 0,
                      fontWeight: 600,
                      px: 1,
                      py: '2px',
                      verticalAlign: 'middle',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        minWidth: 0,
                        pl: `${firstCellPl - 8}px`,
                      }}
                    >
                      <IconButton size="small" sx={{ p: 0, flexShrink: 0 }}>
                        {isCollapsed ? (
                          <KeyboardArrowRight fontSize="small" />
                        ) : (
                          <KeyboardArrowDown fontSize="small" />
                        )}
                      </IconButton>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ flexShrink: 0 }}
                      >
                        {caption}:&nbsp;
                      </Typography>
                      <Typography
                        component="span"
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          overflow: wordWrap ? 'visible' : 'hidden',
                          textOverflow: wordWrap ? 'clip' : 'ellipsis',
                          whiteSpace: wordWrap ? 'normal' : 'nowrap',
                          overflowWrap: wordWrap ? 'anywhere' : 'normal',
                          wordBreak: wordWrap ? 'break-word' : 'normal',
                          minWidth: 0,
                        }}
                      >
                        {group.label}
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexShrink: 0, ml: 0.5 }}
                      >
                        ({group.totalCount})
                      </Typography>
                      {firstColumnSummary && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0, ml: 1 }}
                        >
                          Sum: {formatSummaryValue(group.sums[firstColumnSummaryField])}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>,
                  ...columns.slice(labelSpan).map((col) => {
                    const meta = columnMetadataMap.get(col.id);
                    const field = (meta?.dataField || meta?.field || '') as string;
                    const isSummary = inlineSummaryColumns.some(
                      (summaryColumn) => summaryColumn.id === col.id
                    );
                    return (
                      <TableCell
                        key={`${group.key}-${col.id}`}
                        sx={{
                          borderLeft: 0,
                          borderRight: 0,
                          fontWeight: 600,
                          px: 1,
                          py: '2px',
                          verticalAlign: 'middle',
                        }}
                      >
                        {isSummary ? `Sum: ${formatSummaryValue(group.sums[field])}` : ''}
                      </TableCell>
                    );
                  }),
                ];
              })()}
            </TableRow>

            {/* ── Children: nested groups OR leaf rows ─────────────────── */}
            {!isCollapsed &&
              (isLeaf ? (
                /* Leaf level: render actual data rows */
                group.rows.map((row, rowIndex) => (
                  <DataRow
                    key={row.id}
                    row={row}
                    rowIndex={groupRowOffset + rowIndex}
                    isSelected={row.getIsSelected()}
                    columnVisibility={columnVisibility}
                    columnOrder={columnOrder}
                    modifiedCells={modifiedCells}
                    markedForDeletion={markedForDeletion}
                    validationErrors={table.options.meta?.validationErrors}
                    table={table}
                    rowHeight={rowHeight}
                    wordWrap={wordWrap}
                    hiddenColumnIds={hiddenColumnIds}
                  />
                ))
              ) : (
                /* Non-leaf: recurse one level deeper */
                <GroupRows
                  groups={group.children}
                  depth={depth + 1}
                  rowOffset={groupRowOffset}
                  table={table}
                  collapsedGroups={collapsedGroups}
                  onToggleGroup={onToggleGroup}
                  columnMetadataMap={columnMetadataMap}
                  groupCaptions={groupCaptions}
                  modifiedCells={modifiedCells}
                  markedForDeletion={markedForDeletion}
                  rowHeight={rowHeight}
                  wordWrap={wordWrap}
                  hiddenColumnIds={hiddenColumnIds}
                />
              ))}

            {/* ── Footer sum row (only when showInGroupFooter = true) ───── */}
            {!isCollapsed && footerSumColumns.length > 0 && (
              <TableRow
                sx={{
                  backgroundColor: (theme) =>
                    `rgba(${theme.palette.mode === 'dark' ? '255,255,255' : '0,0,0'},${rowAlpha(depth, theme.palette.mode === 'dark')})`,
                }}
              >
                {columns.map((col, i) => {
                  const meta = columnMetadataMap.get(col.id);
                  const field = (meta?.dataField || meta?.field || '') as string;
                  const showSum =
                    !!field && isGroupSummaryColumn(meta) && getShowInGroupFooter(meta!);
                  return (
                    <TableCell
                      key={col.id}
                      style={{
                        width: getColumnWidth(col),
                        padding: '2px 8px',
                        paddingLeft: i === 0 ? `${firstCellPl}px` : undefined,
                      }}
                      sx={{
                        fontWeight: 600,
                        color: (theme) =>
                          theme.palette.mode === 'dark' ? 'text.primary' : 'text.secondary',
                      }}
                    >
                      {i === 0 ? 'Sum:' : showSum ? formatSummaryValue(group.sums[field]) : ''}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

export const GroupedDataGridBody = <T extends Record<string, unknown>>(
  props: GroupedDataGridBodyProps<T>
) => {
  const { groupedRows, table, ...rest } = props;
  const columns = table
    .getVisibleLeafColumns()
    .filter((col) => !props.hiddenColumnIds?.has(col.id));

  if (groupedRows.length === 0) {
    return (
      <TableBody>
        <TableRow sx={{ height: '100%' }}>
          <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4, height: '100%' }}>
            <Typography color="text.secondary">No results found</Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      <GroupRows groups={groupedRows} depth={0} rowOffset={0} table={table} {...rest} />
    </TableBody>
  );
};
